import { encodeDivider } from '../components/common/sidebarLayout';

/**
 * Default sidebar order per persona, used whenever a user has no saved order
 * of their own (see `useSidebarOrder`). Divider tokens become the collapsible
 * group headers. Group labels are plain English, like the recent nav labels:
 * a translated divider would oblige regenerating nine locales.
 *
 * Only ORDER lives here. Who may see an entry is Feature Visibility's job, so a
 * path listed for a persona that the role hides simply drops out, and an entry
 * missing from the list is appended at the bottom by `buildSidebarLayout`.
 * A tab group is listed once, by its home path (see `sidebarKey`).
 */
export type Persona = 'pd' | 'marketing' | 'factory';

const PD_ROLES = new Set(['admin', 'pm', 'engineer', 'techWriter']);

/** Marketing: holds `marketing` and no PD role. Factory: holds only `factory`. Everyone else is PD. */
export function personaFor(roles: readonly string[] | undefined): Persona {
  const held = roles ?? [];
  if (held.length > 0 && held.every((r) => r === 'factory')) return 'factory';
  if (held.includes('marketing') && !held.some((r) => PD_ROLES.has(r))) return 'marketing';
  return 'pd';
}

const group = (id: string, label: string) => encodeDivider(id, label);

export const PERSONA_SIDEBAR_ORDER: Record<Persona, string[]> = {
  pd: [
    '/',
    group('pd-products', 'Products'),
    '/products', '/products-spreadsheet', '/reports', '/battery-info', '/pdlc-board', '/barcodes', '/competitors',
    group('pd-testing', 'Testing'),
    '/evaluations', '/asset-tracking', '/fixture-comparison', '/apps',
    group('pd-engineering', 'Engineering'),
    '/bugs', '/dev-dashboard', '/docs',
    group('pd-paperwork', 'Paperwork & samples'),
    '/form-approvals', '/tracking', '/file-transfer',
    group('pd-commercial', 'Commercial'),
    '/price-lists',
    group('pd-marketing', 'Marketing'),
    '/marketing/pipeline', '/marketing/requests', '/marketing/naming', '/bulletins',
  ],
  marketing: [
    '/',
    group('mk-marketing', 'Marketing'),
    '/marketing/pipeline', '/marketing/requests', '/marketing/naming', '/bulletins',
    group('mk-products', 'Products'),
    '/products', '/reports', '/barcodes', '/competitors', '/fixture-comparison', '/asset-tracking', '/file-transfer',
  ],
  factory: ['/', '/products', '/tracking', '/file-transfer'],
};

export function defaultSidebarOrder(roles: readonly string[] | undefined): string[] {
  return PERSONA_SIDEBAR_ORDER[personaFor(roles)];
}
